import { useState } from "react";
import { useAuth } from "./AuthProvider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { LogIn, UserPlus, Eye, EyeOff, Dumbbell, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ROUTES } from "@/constants/routes";

/**
 * Map Supabase auth error strings to friendlier, localised messages.
 * Supabase returns English strings verbatim ("Invalid login credentials",
 * "User already registered", …); surfacing those to NL users is jarring
 * and not actionable. Falls back to the raw message if no key matches
 * so we never blank an error.
 */
function mapAuthError(t: (k: string) => string, raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes("invalid login credentials") || m.includes("invalid_grant")) {
    return t("auth.error_invalid_credentials");
  }
  if (m.includes("user already registered") || m.includes("already exists")) {
    return t("auth.error_email_taken");
  }
  if (m.includes("rate limit") || m.includes("too many")) {
    return t("auth.error_rate_limited");
  }
  // Tightened: bare "confirm" matched "password confirmation does not
  // match", and bare "fetch" caught any "Failed to fetch user…" message.
  // Anchor to the actual Supabase phrases instead.
  if (m.includes("email not confirmed")) {
    return t("auth.error_email_not_confirmed");
  }
  if (m.includes("password") && (m.includes("short") || m.includes("at least"))) {
    return t("auth.error_password_too_short");
  }
  if (m.includes("networkerror") || m.includes("network error") || m.includes("failed to fetch")) {
    return t("auth.error_network");
  }
  return raw;
}

export function AuthForm() {
  const { t } = useTranslation();
  const location = useLocation();
  // /signup opende tot nu toe gewoon het inlogformulier; wie via "Start
  // free" op de voorpagina binnenkwam moest eerst nog op "Aanmaken" tikken.
  const [isSignUp, setIsSignUp] = useState(location.pathname === "/signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Self-service password reset: "recoveryMode" swaps the form into a
  // send-me-a-reset-link flow; "recoverySent" shows the confirmation.
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const { signIn, signUp } = useAuth();

  const openRecovery = () => {
    setErrorMessage(null);
    setPassword("");
    setRecoveryMode(true);
    setRecoverySent(false);
  };

  const closeRecovery = () => {
    setErrorMessage(null);
    setRecoveryMode(false);
    setRecoverySent(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      if (recoveryMode) {
        // Send the Supabase recovery email. The link signs the user into
        // a temporary recovery session and lands on /reset-password.
        const redirectTo = `${window.location.origin}${ROUTES.RESET_PASSWORD}`;
        const { error } = await supabase.auth.resetPasswordForEmail(
          email.trim().toLowerCase(),
          { redirectTo }
        );
        if (error) throw error;
        setRecoverySent(true);
        toast.success(t("auth.recovery_sent_toast"));
      } else if (isSignUp) {
        const { needsEmailConfirmation } = await signUp(email, password);
        if (needsEmailConfirmation) {
          // No session yet — they must confirm via the email link before
          // they can sign in. Tell them to check their inbox instead of
          // "account created, now sign in".
          toast.success(t("auth.toast_check_email"));
          setErrorMessage(t("auth.check_email_hint"));
        } else {
          toast.success(t("auth.toast_account_created"));
          setIsSignUp(false);
        }
      } else {
        await signIn(email, password);
        toast.success(t("auth.toast_welcome_back"));
      }
    } catch (error: unknown) {
      const raw = error instanceof Error ? error.message : t("auth.error_generic");
      const friendly = mapAuthError(t, raw);
      // Persist the error so the user (and our e2e tests) can see it
      // after the toast auto-dismisses.
      setErrorMessage(friendly);
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background relative overflow-hidden">
      {/* Very subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-secondary/10 pointer-events-none" />

      <div className="w-full max-w-md relative z-10 animate-scale-in">
        <div className="glass-card rounded-[24px] shadow-lg p-8 md:p-10">
          {/* iOS-style app icon */}
          <div className="text-center mb-8">
            {recoveryMode && (
              <button
                type="button"
                onClick={closeRecovery}
                className="flex items-center gap-2 min-h-11 text-sm text-muted hover:text-foreground transition-colors font-medium mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("auth.back_to_signin")}
              </button>
            )}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-[22px] gradient-brand shadow-md mb-6">
              <Dumbbell className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              {recoveryMode
                ? t("auth.recovery_title")
                : isSignUp
                  ? t("auth.title_signup")
                  : t("auth.title_signin")}
            </h1>
            <p className="text-muted text-base">
              {recoveryMode
                ? t("auth.recovery_subtitle")
                : isSignUp
                  ? t("auth.subtitle_signup")
                  : t("auth.subtitle_signin")}
            </p>
          </div>

          {recoveryMode && recoverySent ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-2xl border border-brand/30 bg-brand/5 p-5"
              data-testid="recovery-sent"
            >
              <MailCheck className="w-6 h-6 text-brand" />
              <h2 className="mt-4 text-base font-semibold text-foreground">
                {t("auth.recovery_sent_title")}
              </h2>
              <p className="mt-2 text-sm text-muted">
                {t("auth.recovery_sent_body", { email: email.trim().toLowerCase() })}
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={closeRecovery}
                className="mt-5"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("auth.back_to_signin")}
              </Button>
            </div>
          ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5"
            data-testid="auth-form"
            data-auth-mode={recoveryMode ? "recovery" : isSignUp ? "signup" : "signin"}
          >
            <div className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-foreground mb-2 block">
                  {t("auth.email_label")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.email_placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 text-base"
                  data-testid="email-input"
                />
              </div>

              {!recoveryMode && (
              <div>
                <Label htmlFor="password" className="text-sm font-semibold text-foreground mb-2 block">
                  {t("auth.password_label")}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 text-base pr-12"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                    className="tap-target absolute right-4 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              )}

              {!isSignUp && !recoveryMode && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <div className="flex items-center min-h-11">
                    <Checkbox
                      className="tap-target h-6 w-6"
                      id="remember"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                    />
                    <Label htmlFor="remember" className="ml-3 text-sm text-foreground cursor-pointer font-medium">
                      {t("auth.remember_me")}
                    </Label>
                  </div>
                  <button
                    type="button"
                    onClick={openRecovery}
                    className="tap-target py-2.5 text-sm text-brand font-medium hover:underline transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t("auth.forgot_password")}
                  </button>
                </div>
              )}
            </div>

            {errorMessage && (
              <div
                data-testid="auth-error"
                role="alert"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
              >
                {errorMessage}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 gradient-brand text-white font-semibold text-base rounded-[12px] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 mt-8"
              data-testid="submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {recoveryMode
                    ? t("auth.recovery_sending")
                    : isSignUp
                      ? t("auth.creating_account")
                      : t("auth.signing_in")}
                </>
              ) : recoveryMode ? (
                t("auth.recovery_cta")
              ) : (
                <>
                  {isSignUp ? (
                    <>
                      <UserPlus className="w-5 h-5 mr-2" />
                      {t("auth.cta_signup")}
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      {t("auth.cta_signin")}
                    </>
                  )}
                </>
              )}
            </Button>
          </form>
          )}

          {!recoveryMode && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="tap-target py-2.5 text-sm text-muted hover:text-brand transition-colors font-medium"
            >
              {isSignUp ? (
                <>
                  {t("auth.have_account_q")}{" "}
                  <span className="text-brand">{t("auth.have_account_cta")}</span>
                </>
              ) : (
                <>
                  {t("auth.no_account_q")}{" "}
                  <span className="text-brand">{t("auth.no_account_cta")}</span>
                </>
              )}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
